import {
  useInfiniteQuery,
  UseInfiniteQueryResult,
} from "@tanstack/react-query";

import {
  DocumentFilters,
  OrderLineModelDocumentModelDocumentStatusEnum,
  OrderModel,
} from "api";
import { useDocumentService } from "contexts/ServiceContext";
import useSelectedCustomerCode from "hooks/useSelectedCustomerCode";
import { SHIPPING_STATUS } from "services/OrderService";
import { getProductName } from "util/getProductName";
import { formatQuantityValue, valueOr, valueToDate } from "util/statHelpers";
import { AI_EVENTS, useTrackEvent } from "../../useAppInsight";
import useListOfTransportationModes from "../mode-of-transportation/useListOfTransportationModes";

const mapApiOrderValues = (order: OrderModel): DocumentOrderLineItem => {
  const getShippingStatusColumn = (_order: OrderModel) => {
    if (_order.IsCanceled) {
      return SHIPPING_STATUS.CANCELED;
    } else {
      return _order.DocumentStatus ===
        OrderLineModelDocumentModelDocumentStatusEnum.Open
        ? SHIPPING_STATUS.NOT_SHIPPED
        : SHIPPING_STATUS.SHIPPED;
    }
  };

  return {
    docEntry: order.DocEntry,
    releaseNumber: valueOr(order.ReleaseNumber),
    createDate: valueToDate(order.CreateDateTime),
    contractNumber: valueOr(order.ContractNumber),
    poNumber: valueOr(order.NumAtCard),
    productName: getProductName(order.DocumentLines),
    quantity: order.CalculatedSumQuantity ?? 0,
    quantityFormatted: formatQuantityValue(order.CalculatedSumQuantity), // Ideally we should be consistent and use ContractQuantityCommitted, but that is only available if $expand contains "ContractTotals"
    shipFrom: valueOr(order.DocumentLines?.[0]?.WarehouseName),
    shipTo: `${valueOr(order.ShipToCode)}`,
    mode: order.ModeOfTransportationCode,
    salesTerms: valueOr(order.SalesTermsCode),
    shippingStatus: getShippingStatusColumn(order),
  };
};

const mapApiDocumentOrdersFilters = (
  filters?: DocumentsOrdersFilters
): DocumentFilters => {
  return {
    CardCodes: filters?.cardCodes,
    CreateDateEnd: filters?.createDateEndDate,
    CreateDateStart: filters?.createDateStartDate,
    Modes: filters?.mode,
    ProductCodes: filters?.product,
    SalesTerms: filters?.salesTerms,
    SearchString: filters?.searchString,
    ShipToCodes: filters?.shipTo,
    StatusCodes: filters?.status,
    WarehouseCodes: filters?.shipFrom,
  };
};

const useDocumentOrders = (
  currentSort?: { key: keyof OrderModel; direction: string },
  requestBody?: DocumentsOrdersFilters
): UseInfiniteQueryResult<{ orders: DocumentOrders[] }> => {
  const cardCodes = useSelectedCustomerCode();

  const documentService = useDocumentService();

  const { data: modes, isLoading } = useListOfTransportationModes();

  const trackQuery = useTrackEvent(AI_EVENTS.API_REQUEST, {
    cardCodes,
  });

  const trackQueryError = useTrackEvent(AI_EVENTS.API_ERROR, {
    cardCodes,
  });
  return useInfiniteQuery({
    queryKey: ["documentOrders", { currentSort, ...requestBody }],
    queryFn: ({ pageParam = 0 }) => {
      const filterParams = {
        sort: currentSort,
        page: pageParam,
      };
      const eventId = trackQuery({
        endpoint: "Order/SearchNFilterGridView",
        parameters: filterParams,
      });

      return documentService
        .getDocumentOrderList(
          filterParams,
          mapApiDocumentOrdersFilters(requestBody)
        )
        .then((data) => ({
          ...data,
          value: data.value?.map((value) => ({
            ...value,
            ModeOfTransportationCode:
              modes?.find(
                (mode) => mode.code === value.ModeOfTransportationCode
              )?.name ?? value.ModeOfTransportationCode,
          })),
        }))
        .then(({ value, ...rest }) => ({
          ...rest,
          orders: value?.map(mapApiOrderValues) ?? [],
        }))
        .catch((err) => {
          trackQueryError(
            {
              statusCode: err.statusCode as number,
              message: err.message,
            },
            eventId
          );
          throw err;
        });
    },
    enabled: !isLoading,
  });
};

export default useDocumentOrders;
